import React, { useState, useEffect, useCallback } from "react";
import {
  RefreshCw,
  Play,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Zap,
} from "lucide-react";
import { PrimaryButton, SecondaryButton } from "../common/ActionButtons";
import { useAuth } from "../../contexts/AuthContext";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const StatusBadge = ({ status }) => {
  const styles = {
    delivered: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-800",
    retrying: "bg-yellow-100 text-yellow-800",
    pending: "bg-blue-100 text-blue-800",
  };
  const icons = {
    delivered: CheckCircle,
    failed: XCircle,
    retrying: RefreshCw,
    pending: Clock,
  };
  const Icon = icons[status] || Clock;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${styles[status] || styles.pending}`}
    >
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
};

const WebhookManagement = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [facilityFilter, setFacilityFilter] = useState("");
  const [testUrl, setTestUrl] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [retryResult, setRetryResult] = useState(null);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 25 });
      if (statusFilter) params.set("status", statusFilter);
      if (facilityFilter) params.set("facility_id", facilityFilter);

      const res = await fetch(`${AUTH_URL}/webhooks?${params}`, { headers });
      const data = await res.json();
      setEvents(data.events || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Failed to load webhook events:", err);
    }
    setLoading(false);
  }, [page, statusFilter, facilityFilter, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${AUTH_URL}/webhooks/summary`, { headers });
      const data = await res.json();
      setSummary(data);
    } catch (err) {
      console.error("Failed to load webhook summary:", err);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchEvents();
    fetchSummary();
  }, [fetchEvents, fetchSummary]);

  const handleReplay = async (eventId) => {
    try {
      const res = await fetch(`${AUTH_URL}/webhooks/${eventId}/replay`, {
        method: "POST",
        headers,
      });
      const data = await res.json();
      if (data.success) {
        fetchEvents();
        fetchSummary();
      }
    } catch (err) {
      console.error("Replay failed:", err);
    }
  };

  const handleRetryAll = async () => {
    try {
      const res = await fetch(`${AUTH_URL}/webhooks/retry-pending`, {
        method: "POST",
        headers,
      });
      const data = await res.json();
      setRetryResult(data);
      fetchEvents();
      fetchSummary();
      setTimeout(() => setRetryResult(null), 5000);
    } catch (err) {
      console.error("Retry-all failed:", err);
    }
  };

  const handleTestPing = async () => {
    if (!testUrl.trim()) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await fetch(`${AUTH_URL}/webhooks/test-ping`, {
        method: "POST",
        headers,
        body: JSON.stringify({ url: testUrl }),
      });
      setTestResult(await res.json());
    } catch (err) {
      setTestResult({ reachable: false, error: err.message });
    }
    setTestLoading(false);
  };

  const totals = summary?.totals || {};

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          {
            label: "Total Events",
            value: Object.values(totals).reduce((a, b) => a + b, 0),
            color: "blue",
          },
          {
            label: "Delivered",
            value: totals.delivered || 0,
            color: "green",
          },
          { label: "Failed", value: totals.failed || 0, color: "red" },
          {
            label: "Retrying",
            value: totals.retrying || 0,
            color: "yellow",
          },
          { label: "Pending", value: totals.pending || 0, color: "slate" },
        ].map((card) => (
          <div
            key={card.label}
            className={`bg-${card.color}-50 rounded-lg p-4 border border-${card.color}-200`}
          >
            <p className={`text-xs text-${card.color}-600 mb-1`}>
              {card.label}
            </p>
            <p className={`text-2xl font-bold text-${card.color}-900`}>
              {card.value}
            </p>
          </div>
        ))}
      </div>

      {/* Test Ping */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">
          Test Webhook Endpoint
        </h3>
        <div className="flex gap-3">
          <input
            type="url"
            value={testUrl}
            onChange={(e) => setTestUrl(e.target.value)}
            placeholder="https://example.com/webhook"
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <PrimaryButton
            onClick={handleTestPing}
            icon={Send}
            disabled={testLoading}
          >
            {testLoading ? "Testing..." : "Send Test Ping"}
          </PrimaryButton>
        </div>
        {testResult && (
          <div
            className={`mt-3 p-3 rounded-lg text-sm ${testResult.reachable ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}
          >
            {testResult.reachable ? (
              <span>Reachable — HTTP {testResult.status_code}</span>
            ) : (
              <span>
                Not reachable —{" "}
                {testResult.error || `HTTP ${testResult.status_code}`}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Retry Result Banner */}
      {retryResult && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          Retry batch: {retryResult.attempted} attempted,{" "}
          {retryResult.delivered} delivered, {retryResult.failed} failed
        </div>
      )}

      {/* Filters & Actions */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-slate-200">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex gap-3 items-center">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              <option value="">All Statuses</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
              <option value="retrying">Retrying</option>
              <option value="pending">Pending</option>
            </select>
            <input
              type="text"
              value={facilityFilter}
              onChange={(e) => {
                setFacilityFilter(e.target.value);
                setPage(1);
              }}
              placeholder="Filter by facility_id"
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm w-40"
            />
            <span className="text-sm text-slate-500">{total} events</span>
          </div>
          <div className="flex gap-2">
            <SecondaryButton onClick={handleRetryAll} icon={RefreshCw}>
              Retry All Pending
            </SecondaryButton>
            <SecondaryButton
              onClick={() => {
                fetchEvents();
                fetchSummary();
              }}
              icon={RefreshCw}
            >
              Refresh
            </SecondaryButton>
          </div>
        </div>
      </div>

      {/* Events Table */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                {[
                  "ID",
                  "Facility",
                  "Event",
                  "URL",
                  "Status",
                  "Attempts",
                  "Created",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
                  </td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-slate-500"
                  >
                    No webhook events found
                  </td>
                </tr>
              ) : (
                events.map((evt) => (
                  <tr key={evt.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-mono text-slate-600">
                      #{evt.id}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {evt.facility_id || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs font-medium">
                        {evt.event_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-[200px] truncate">
                      {evt.webhook_url}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={evt.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {evt.attempts}/{evt.max_attempts}
                      {evt.last_error && (
                        <div
                          className="text-xs text-red-500 mt-0.5 truncate max-w-[150px]"
                          title={evt.last_error}
                        >
                          {evt.last_error}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {evt.created_at
                        ? new Date(evt.created_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {(evt.status === "failed" ||
                        evt.status === "retrying") && (
                        <button
                          onClick={() => handleReplay(evt.id)}
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                          title="Replay this webhook"
                        >
                          <Play className="w-3.5 h-3.5" />
                          Replay
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
            <span className="text-sm text-slate-600">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Per-Facility Breakdown */}
      {summary?.by_facility?.length > 0 && (
        <div className="bg-white rounded-lg shadow-md border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800">
              Delivery by Facility
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  {[
                    "Facility",
                    "Total",
                    "Delivered",
                    "Failed",
                    "Retrying",
                    "Rate",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {summary.by_facility.map((row) => (
                  <tr key={row.facility_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-medium text-slate-700">
                      {row.facility_id}
                    </td>
                    <td className="px-4 py-3 text-sm">{row.total_events}</td>
                    <td className="px-4 py-3 text-sm text-green-700">
                      {row.delivered}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-700">
                      {row.failed}
                    </td>
                    <td className="px-4 py-3 text-sm text-yellow-700">
                      {row.retrying}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`font-medium ${parseFloat(row.delivery_rate) >= 90 ? "text-green-700" : parseFloat(row.delivery_rate) >= 70 ? "text-yellow-700" : "text-red-700"}`}
                      >
                        {row.delivery_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookManagement;
